<template>
    <div class="supsis-plan-card" :class="{ selected: isActive }">
        <div class="supsis-plan-card-head" :class="{ popular: plan.isPopular }">
            <div class="supsis-plan-card-head-title">
                <span class="plan-title">{{ plan.altTitle }}</span>
                <span v-if="plan.isPopular" class="plan-badge">{{ $t("billing.popular") }}</span>
                <span v-if="isActive" class="plan-badge">{{ $t("billing.selectedPlan") }}</span>
            </div>

            <div class="supsis-plan-card-head-pricing">
                <template v-if="!plan.isEnterPrise && !isGroupPlan">
                    <div class="supsis-plan-card-head-pricing-detail">
                        {{ getPerMonthPrice() <= 0 ? "Free" : `${getPerMonthPrice()}₺` }} <span class="discount"
                            v-if="getPerMonthPrice(true) > getPerMonthPrice()">
                            {{ getPerMonthPrice(true) }}₺
                            </span>
                    </div>
                    <div class="supsis-plan-card-head-pricing-period">
                        <span v-if="period !== 'monthly'"> {{ getPrice(plan, details, period) }}₺ /</span>
                        {{ getPricePeriod() }}
                    </div>
                </template>
            </div>

            <div class="supsis-plan-card-head-actions" v-if="editableCard">
                <SupsisButton primary @click="editPlan"> {{ $t("billing.editPlan") }}</SupsisButton>
                <SupsisButton danger @click="deletePlan">{{ $t("billing.removePlan") }}</SupsisButton>
                <SupsisButton primary @click="visibilityChange" v-if="selfSite">
                    <template v-if="isHidden()"> {{ $t("billing.showPlan") }}</template>
                    <template v-else>{{ $t("billing.hiddenPlan") }}</template>
                </SupsisButton>
            </div>
            <div class="supsis-plan-card-head-actions" v-else>
                <CardSelectFeature :plan="plan" :period="period" :selected-feature-index="selectedFeatureIndex"
                    :current-plans="currentPlans" @selectedFeature="onSelectedFeature" />
                <SupsisButton primary @click="gotoGroupPlan" v-if="isGroupPlan">{{ $t("billing.viewDetails") }}
                </SupsisButton>
                <SupsisButton @click="gotoContactSales" v-else-if="plan.isEnterPrise" primary>
                    {{ $t("billing.contactSales") }}
                </SupsisButton>
                <SupsisButton primary :disabled="!isUpgrade && !isBlockedByResourceLimit" @click="subscribePlan" v-else>
                    {{ buttonText }}
                </SupsisButton>
            </div>
        </div>
        <div class="supsis-plan-card-body">
            <div class="supsis-plan-card-body-content">{{ $t("billing.features") }}</div>
            <div class="supsis-plan-card-body-features">
                <template v-if="Object.keys(getOtherFeatures()).length > 0">
                    <CardFeatureText v-for="(key, index) in Object.keys(getOtherFeatures())" :value="key"
                        :features="getOtherFeatures()" :key="getOtherFeatures()[key]" />
                </template>
                <template v-if="isGroupPlan">
                    <CardFeatureText value="chipPlans" :features="features" />
                    <CardFeatureText value="rightToCancel" :features="features" />
                    <CardFeatureText v-if="plan.type === 'supsis'" value="stableAgentCount" :features="features" />
                    <CardFeatureText
                        v-if="['instagram', 'messenger', 'whatsapp', 'whatsapp-business'].includes(plan.type)"
                        value="stableChannelCount" :features="features" />
                </template>
                <CardFeatureText v-else v-for="(key, index) in Object.keys(features).slice(
                    0,
                    moreFeatures
                        ? Object.keys(features).length + Object.keys(getOtherFeatures()).length
                        : 5 - Object.keys(getOtherFeatures()).length
                )" :value="key" :features="features" :key="key" />
            </div>
        </div>
        <div class="supsis-plan-card-footer" v-if="Object.keys(features).length > 5" @click="toggleMore">
            <div class="material-icons">
                {{ moreFeatures ? "arrow_drop_up" : "arrow_drop_down" }}
            </div>
            <template v-if="moreFeatures"> {{ $t("billing.showLess") }}</template>
            <template v-else> +{{ Object.keys(features).length - 5 }} {{ $t("billing.more") }}</template>
        </div>

        <PlanLimitWarningModal
            :visible="usageWarningVisible"
            :planType="plan.type"
            :isCancel="isActive"
            @close="usageWarningVisible = false"
        />
    </div>
</template>

<script>
import { mapState } from "vuex";
import log2 from "../../utils/log2";
import { Features } from "./feature-items";
import CodeInput from "./CodeInput.vue";
import _ from "lodash";
import CardFeatureText from "./CardFeatureText.vue";
import { calcDiscount, calcPrice, getProductQuantity } from "./utils/PriceCalc";
import CardSelectFeature from "./CardSelectFeature.vue";
import SupsisButton from "../../views/Pages/Settings/components/SupsisButton.vue";
import PlanLimitWarningModal from "./PlanLimitWarningModal.vue";

const log = log2("Card");
export default {
    name: "Card",
    components: {
        SupsisButton,
        CardSelectFeature,
        CardFeatureText,
        CodeInput,
        PlanLimitWarningModal,
    },
    props: {
        plan: {
            type: Object,
            default: {},
        },
        period: {
            type: String,
            required: false,
            default: "monthly",
        },
        details: {
            type: Object,
            default: () => ({
                agentCount: 1,
                whatsappChannel: 1,
                messengerChannel: 1,
                instagramChannel: 1,
                trendyolChannel: 1,
                metaLeadChannel: 1,
                maxUsageCount: 0,
                channelCount: 0,
            }),
        },
        editableCard: {
            type: Boolean,
            default: false,
        },
        selfSite: {
            type: Boolean,
            default: false,
        },
        wizard: {
            type: Boolean,
            default: false,
        },
        isSelected: {
            type: Boolean,
            default: false,
        },
        appsumoCoupons: {
            type: Array,
            default: () => [],
        },
        currentPlans: {
            type: Array,
            default: () => [],
        },
        plans: {
            type: Array,
            default: () => [],
        },
        isAdmin: {
            type: Boolean,
            default: false,
        },
        visibilityDetails: {
            type: Object,
            default: () => ({}),
        },
        isGroupPlan: {
            type: Boolean,
            default: false,
        },
        isReadonly: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState({
            systemLanguages: (state) => state.setting.settings.systemLanguages,
            site: (state) => state.site.site,
            activeUserCount: (state) => state.user.activeUserCount,
            channels: (state) => state.channel.channels,
            siteFeatures: (state) => state.site.features,
            conversationStatistics: (state) =>
                (state.site.conversationStatistics.monthlyUsages || []).reduce(
                    (acc, usage) => {
                        acc.chatbotUsage += usage.chatbotUsage;
                        acc.automationTaskCounter += usage.automationTaskCounter;
                        acc.emailCount += usage.emailCount;
                        acc.uniqueEmailCount += usage.uniqueEmailCount;
                        acc.totalWebVisits += usage.totalWebVisits;
                        return acc;
                    },
                    {
                        chatbotUsage: 0,
                        automationTaskCounter: 0,
                        emailCount: 0,
                        uniqueEmailCount: 0,
                        totalWebVisits: 0,
                    }
                ),
        }),
        isUpgrade() {
            console.log("isUpgrade", this.plan, this.currentPlan, this.currentPlansHash);
            
            /**
             * Birden fazla plan mantığı:
             * 1. Aylık plan almaya çalışıyorsa VE aylık olmayan plan varsa → ENGELLE
             * 2. Yıllık/dönemsel plan alabilir (aylık plan varsa store.js'de iptal edilecek)
             * 3. Takım planları birden fazla yıllık olamaz → ENGELLE
             * 4. Aynı plan-period kombinasyonu varsa → ENGELLE
             * 5. Free planlar iptal edilemez → ENGELLE
             */
            
            // Sadece bu plan tipine ait aktif planları filtrele
            const allCurrentPlans = this.currentPlans || [];
            const activePlansForThisType = allCurrentPlans.filter(cp => 
                cp.planType === this.plan.type || (this.plan.dynamicType && cp.planType === this.plan.dynamicType)
            );
            
            // Aylık plan almaya çalışıyorsa, aylık OLMAYAN bir plan kontrolü
            if (this.period === "monthly") {
                const hasNonMonthlyPlan = activePlansForThisType.some(cp => cp.period !== "monthly");
                
                if (hasNonMonthlyPlan) {
                    return false;
                }
            }
            
            // Takım planları birden fazla yıllık/dönemsel plan alamaz
            if (this.plan.isTeamPlan && this.period !== "monthly") {
                const hasNonMonthlyTeamPlan = activePlansForThisType.some(cp => cp.period !== "monthly");
                
                if (hasNonMonthlyTeamPlan) {
                    return false;
                }
            }
            
            // Aynı plan-period kombinasyonu varsa kontrol et
            if (this.isActive) {
                // Free planlar iptal edilemez
                if (this.plan.isFreePlan) {
                    return false;
                }
                
                // Yıllık/dönemsel planlar iptal edilemez (birden fazla olamaz kuralı nedeniyle)
                if (this.period !== "monthly") {
                    return false;
                }
                
                // Aylık plan ise, aylık olmayan plan varsa iptal edemez
                const hasNonMonthlyPlan = activePlansForThisType.some(cp => cp.period !== "monthly");
                if (hasNonMonthlyPlan) {
                    return false;
                }
                
                // Sadece aylık planları var, iptal edebilir (isUpgrade true olsun)
            }
            
            const periods = ["monthly", "monthly-3", "monthly-6", "annual", "annual-2"];
            const currentPlanAction = this.currentPlansHash[this.currentPlan?._id];
            const wpChannels = this.channels.filter((channel) => ["whatsapp-qr", "whatsapp-v2"].includes(channel.type));
            const businessChannels = this.channels.filter((channel) => channel.type === "whatsapp-business-api" || channel.type === "whatsapp-business-api-supsis");
            const messengerChannels = this.channels.filter((channel) => channel.type === "messenger");
            const instagramChannels = this.channels.filter((channel) => ["instagram", "instagram-v2"].includes(channel.type));
            const trendyolChannels = this.channels.filter((channel) => channel.type === "trendyol");
            const metaLeadChannels = this.channels.filter((channel) => channel.type === "meta-leads");
            const telegramChannels = this.channels.filter((channel) => channel.type === "telegram");
            const telegramBotFather = this.$store.getters["setting/telegramEnabled"] ? 1 : 0;
            const linkedinChannels = this.channels.filter((channel) => channel.type === "linkedin");
            const webphoneChannels = this.channels.filter((channel) => channel.type === "webphone");
            //const isAppsumoPackage = this.plan.partnerKey === "appsumo";

            const planRules = {
                supsis: () => {
                    let oldAgentCount = parseInt(currentPlanAction?.details?.agentCount || 0);
                    const currentPlanFeature = (this.currentPlan.features || []).find((f) => f.type === "agent");
                    if (currentPlanFeature) {
                        if (this.currentPlan?.isTeamPlan) {
                            oldAgentCount = +currentPlanFeature.agentCount;
                        }
                        const freeAgentCount = +currentPlanFeature?.freeAgentCount || 0;
                        oldAgentCount += freeAgentCount;
                    }

                    const featureAgentCount = +(this.features.agentCount || 0);
                    const freeAgentCount = +(this.features.freeAgentCount || 0);
                    const newAgentCount =
                        +(this.plan?.isTeamPlan ? featureAgentCount : parseInt(this.details.agentCount)) +
                        freeAgentCount;
                    return (
                        this.activeUserCount <= (this.siteFeatures.maxAgentCount || 0) + (newAgentCount - oldAgentCount)
                    );
                },
                whatsapp: () => {
                    const oldChannelsCount = parseInt(currentPlanAction?.details?.whatsappChannel || 0);
                    const newChannelsCount = this.details.whatsappChannel;
                    return (
                        wpChannels.length <=
                        (this.siteFeatures.maxWhatsappChannel || 0) + (newChannelsCount - oldChannelsCount)
                    );
                },
                "whatsapp-business": () => {
                    const oldChannelsCount = parseInt(currentPlanAction?.details?.whatsappChannel || 0);
                    const newChannelsCount = this.details.whatsappChannel;
                    return (
                        businessChannels.length <=
                        (this.siteFeatures.maxBusinessWhatsappChannel || 0) + (newChannelsCount - oldChannelsCount)
                    );
                },
                instagram: () => {
                    const oldChannelsCount = parseInt(currentPlanAction?.details?.instagramChannel || 0);
                    const newChannelsCount =
                        (this.details.instagramChannel || 0) + (this.features.instagramChannelCount || 0);
                    return (
                        instagramChannels.length <=
                        (this.siteFeatures.maxInstagramChannel || 0) + (newChannelsCount - oldChannelsCount)
                    );
                },
                messenger: () => {
                    const oldChannelsCount = parseInt(currentPlanAction?.details?.messengerChannel || 0);
                    const newChannelsCount =
                        (this.details.messengerChannel || 0) + (this.features.messengerChannelCount || 0);
                    return (
                        messengerChannels.length <=
                        (this.siteFeatures.maxMessengerChannel || 0) + (newChannelsCount - oldChannelsCount)
                    );
                },
                trendyol: () => {
                    const oldChannelsCount = parseInt(currentPlanAction?.details?.trendyolChannel || 0);
                    const newChannelsCount =
                        (this.details.trendyolChannel || 0) + (this.features.trendyolChannelCount || 0);
                    return (
                        trendyolChannels.length <=
                        this.siteFeatures.maxTrendyolChannel + (newChannelsCount - oldChannelsCount)
                    );
                },
                "meta-lead": () => {
                    const oldChannelsCount = parseInt(currentPlanAction?.details?.metaLeadChannel || 0);
                    const newChannelsCount =
                        (this.details.metaLeadChannel || 0) + (this.features.metaLeadChannelCount || 0);
                    return (
                        metaLeadChannels.length <=
                        this.siteFeatures.maxMetaLeadChannel + (newChannelsCount - oldChannelsCount)
                    );
                },
                chatbot: () => {
                    const oldPlanFeatures =
                        (this.currentPlan?.features || []).find((f) => f.type === "chatbot")?.maxSessionCount || 0;
                    const newPlanFeatures = this.features.maxSessionCount || 0;

                    return (
                        this.conversationStatistics.chatbotUsage < newPlanFeatures ||
                        this.conversationStatistics.chatbotUsage <=
                        this.siteFeatures.maxSessionCount + (newPlanFeatures - oldPlanFeatures)
                    );
                },
                automation: () => {
                    const oldTaskCount = currentPlanAction?.details?.maxUsageCount || 0;
                    const newTaskCount = _.get(this.plan, `selectFeatures[${this.selectedFeatureIndex}].count`, 0);
                    return (
                        this.conversationStatistics.automationTaskCounter < newTaskCount ||
                        this.conversationStatistics.automationTaskCounter <=
                        parseInt(this.siteFeatures.maxAutomationTask) +
                        (parseInt(newTaskCount) - parseInt(oldTaskCount))
                    );
                },
                web: () => {
                    const oldTaskCount = currentPlanAction?.details?.maxUsageCount || 0;
                    const newTaskCount = _.get(this.plan, `selectFeatures[${this.selectedFeatureIndex}].count`, 0);
                    return (
                        this.conversationStatistics.totalWebVisits < newTaskCount ||
                        this.conversationStatistics.totalWebVisits <=
                        parseInt(this.siteFeatures.maxWebTrafficCount) +
                        (parseInt(newTaskCount) - parseInt(oldTaskCount))
                    );
                },
                mail: () => {
                    const oldEmailCount = currentPlanAction?.details?.maxUsageCount || 0;
                    const oldUniqMailCount = currentPlanAction?.details?.maxEmailSentContact || 0;
                    const maxMailCount = _.get(this.plan, `selectFeatures[${this.selectedFeatureIndex}].count`, 0);
                    const maxUniqMailCount = _.get(
                        this.plan,
                        `selectFeatures[${this.selectedFeatureIndex}].details.uniqEmailContactCount`,
                        0
                    );

                    const isUsageLimit =
                        this.conversationStatistics.emailCount < maxMailCount &&
                        this.conversationStatistics.uniqueEmailCount < maxUniqMailCount;

                    const isLimited =
                        this.conversationStatistics.emailCount <=
                        this.siteFeatures.maxMailCount + (maxMailCount - oldEmailCount) &&
                        this.conversationStatistics.uniqueEmailCount <=
                        this.siteFeatures.maxEmailSentContact + (maxUniqMailCount - oldUniqMailCount);

                    return isLimited && isUsageLimit;
                },
                telegram: () => {
                    const oldChannelsCount = parseInt(currentPlanAction?.details?.telegramChannel || 0);
                    const newChannelsCount = this.details.telegramChannel;
                    return (
                        telegramChannels.length + telegramBotFather <=
                        (this.siteFeatures.maxTelegramChannel || 0) + (newChannelsCount - oldChannelsCount)
                    );
                },
                linkedin: () => {
                    const oldChannelsCount = parseInt(currentPlanAction?.details?.linkedinChannel || 0);
                    const newChannelsCount = this.details.linkedinChannel;
                    return (
                        linkedinChannels.length <=
                        (this.siteFeatures.maxLinkedinChannel || 0) + (newChannelsCount - oldChannelsCount)
                    );
                },
                "webphone": () => {
                    const oldChannelsCount = parseInt(currentPlanAction?.details?.channelCount || 0);
                    const newChannelsCount = this.details.channelCount;
                    return (
                        webphoneChannels.length <=
                        (this.siteFeatures.maxCallCenterChannel || 0) + (newChannelsCount - oldChannelsCount)
                    );
                },
            };

            /**
             * Birden fazla plan için upgrade mantığı:
             * - Wizard, Admin veya GroupPlan ise her zaman true
             * - Kapasite kurallarını kontrol et (planRules)
             * - Period ve fiyat kontrolü artık gerekmiyor çünkü aynı tipte farklı periodlarda plan alınabilir
             */
            return (
                this.isGroupPlan ||
                this.wizard ||
                this.isAdmin ||
                (!planRules[this.plan.type] || planRules[this.plan.type]())
            );
        },
        isActive() {
            if (this.isGroupPlan || this.wizard) {
                return this.isSelected;
            }

            /**
             * Birden fazla plan aktif olabilir, bu yüzden currentPlans içinde
             * bu plan ve period kombinasyonunu arıyoruz
             */
            const allCurrentPlans = this.currentPlans || [];
            
            // Sadece bu plan tipine ait aktif planları filtrele
            const activePlansForThisType = allCurrentPlans.filter(cp => 
                cp.planType === this.plan.type || (this.plan.dynamicType && cp.planType === this.plan.dynamicType)
            );
            
            const detailKeys = {
                supsis: "agentCount",
                whatsapp: "whatsappChannel",
                "whatsapp-business": "whatsappChannel",
                instagram: "instagramChannel",
                messenger: "messengerChannel",
                trendyol: "trendyolChannel",
                "meta-lead": "metaLeadChannel",
                telegram: "telegramChannel",
                linkedin: "linkedinChannel",
                "webphone": "channelCount",
            };

            // Bu plan tipindeki tüm aktif planları kontrol et
            const matchingPlan = activePlansForThisType.find(planAction => {
                const eqPlanId = (planAction.planId || "").toString() === (this.plan._id || "").toString();
                const eqPeriod = planAction.period === this.period;
                
                if (!eqPlanId || !eqPeriod) {
                    return false;
                }

                // Detail kontrolü (Tier planlar için feature kontrolü dahil)
                const tierPlanTypes = ["automation", "mail", "web"];
                if (tierPlanTypes.includes(this.plan.type)) {
                    const selectedFeature = this.plan.selectFeatures[this.selectedFeatureIndex];
                    const maxUsageCount = selectedFeature?.count || 0;
                    // Aynı plan, aynı period, aynı feature kontrolü
                    return planAction.details?.maxUsageCount === maxUsageCount;
                }
                
                const detailKey = detailKeys[this.plan.type];
                if (detailKey) {
                    return planAction.details?.[detailKey] === this.details[detailKey];
                }
                
                return true;
            });

            return !!matchingPlan;
        },
        isBlockedByResourceLimit() {
            if (this.isUpgrade) return false;
            if (this.isGroupPlan || this.wizard || this.isAdmin) return false;

            const allCurrentPlans = this.currentPlans || [];
            const activePlansForThisType = allCurrentPlans.filter(function (cp) {
                return cp.planType === this.plan.type || (this.plan.dynamicType && cp.planType === this.plan.dynamicType);
            }.bind(this));

            if (this.isActive) {
                if (this.plan.isFreePlan) return false;
                if (this.period !== "monthly") return false;
                var hasNonMonthlyPlan = activePlansForThisType.some(function (cp) { return cp.period !== "monthly"; });
                if (hasNonMonthlyPlan) return false;
            } else {
                if (this.period === "monthly") {
                    var hasNonMonthlyPlanCheck = activePlansForThisType.some(function (cp) { return cp.period !== "monthly"; });
                    if (hasNonMonthlyPlanCheck) return false;
                }
                if (this.plan.isTeamPlan && this.period !== "monthly") {
                    var hasNonMonthlyTeamPlan = activePlansForThisType.some(function (cp) { return cp.period !== "monthly"; });
                    if (hasNonMonthlyTeamPlan) return false;
                }
            }

            return true;
        },
        buttonText() {
            if (this.wizard && this.isSelected) {
                return this.$t("billing.cancelIt");
            } else if (!this.wizard && this.isActive) {
                // currentPlans zaten aktif planları içeriyor, enabled kontrolü gereksiz
                return this.$t("billing.cancelIt");
            } else if (!this.wizard && !this.isAdmin && !this.isUpgrade && !this.isBlockedByResourceLimit) {
                return this.$t("billing.noTransition");
            } else {
                return this.$t("billing.selectPlan");
            }
        },
    },
    data() {
        return {
            currentPlansHash: {},
            currentPlan: {},
            features: {},
            couponCode: null,
            selectedFeatureIndex: 0,
            moreFeatures: false,
            Features,
            usageWarningVisible: false,
        };
    },
    mounted() {
        this.initData();
        this.features = this.getPlanFeatures();
        this.couponCode = !this.editableCard && this.appsumoCoupons[this.plan.requiredCodes - 1]?.code;
        const currentPlan = this.currentPlansHash[this.plan._id];
        const selectedIndex = this.plan.selectFeatures?.findIndex(
            (selectFeature) => +selectFeature.count === +currentPlan?.details?.maxUsageCount
        );
        if (selectedIndex >= 0) this.selectedFeatureIndex = selectedIndex;
    },
    methods: {
        getOtherFeatures() {
            const selectFeatures = this.plan.selectFeatures || [];
            const selectedFeatureIndex = this.selectedFeatureIndex;
            const selectedFeature = selectFeatures[selectedFeatureIndex];
            const features = {};
            if (!selectedFeature) return features;

            if (this.plan.type === "mail") {
                let { uniqEmailContactCount = 0 } = selectedFeature.details || {};
                features["uniqEmailContactCount"] = uniqEmailContactCount;
            }
            return features;
        },
        toggleMore() {
            this.moreFeatures = !this.moreFeatures;
        },
        initData() {
            if (!this.wizard) {
                const activePlans = this.currentPlans || [];
                this.currentPlansHash = activePlans.reduce((acc, planAction) => {
                    acc[planAction.planId] = planAction;
                    return acc;
                }, {});
                this.currentPlan = (this.plans || [])
                    .filter(
                        (plan) =>
                            this.plan &&
                            (this.plan.type === "feature"
                                ? plan.dynamicType === this.plan.dynamicType
                                : plan.type === this.plan.type)
                    )
                    .find((plan) => this.currentPlansHash[plan._id]) || {};
            }
        },
        getPerMonthPrice(discount) {
            const price = discount ? this.getDiscount(this.plan) : this.getPrice(this.plan, this.details, this.period);
            const period = {
                monthly: 1,
                "monthly-3": 3,
                "monthly-6": 6,
                annual: 12,
                "annual-2": 24,
            };
            let monthlyPrice = price / period[this.period];
            monthlyPrice = monthlyPrice % 1 === 0 ? monthlyPrice : monthlyPrice.toFixed(2);
            return monthlyPrice;
        },
        getPricePeriod() {
            return {
                monthly: "Aylık",
                "monthly-3": "3 Aylık",
                "monthly-6": "6 Aylık",
                annual: "Yıllık",
                "annual-2": "2 Yıllık",
            }[this.period];
        },
        getPrice(plan, details, period) {
            const clonePlan = _.cloneDeep(plan);
            const selectedTier = _.get(clonePlan, `selectFeatures[${this.selectedFeatureIndex}]`);
            const selectedTierPrice = _.get(selectedTier, `price`);
            clonePlan.price = selectedTier ? selectedTierPrice : clonePlan.price;
            let price = clonePlan.price - (clonePlan.price * clonePlan.couponDiscount || 0) / 100;
            let cloneDetails = _.cloneDeep(details || { agentCount: 0, whatsappChannel: 0 });
            if (clonePlan?.isTeamPlan) cloneDetails = {};
            const discounts = {
                monthly: 0,
                "monthly-3": clonePlan.threeMonthlyDiscount,
                "monthly-6": clonePlan.sixMonthlyDiscount,
                annual: clonePlan.yearlyDiscount,
                "annual-2": clonePlan.twoYearDiscount,
                forever: 0,
            };

            let count = getProductQuantity(cloneDetails, clonePlan.type);
            if (!count) count = getProductQuantity({}, "default");
            if (clonePlan.isAppsumo) period = "forever";
            const discountedPrice = calcPrice(price, period, count, discounts[period]);
            if (discountedPrice) price = discountedPrice;
            return price;
        },
        getDiscount(plan) {
            const clonePlan = _.cloneDeep(plan);
            let price = clonePlan.isTrialPlan ? clonePlan.orjinalPrice : clonePlan.price;
            const selectedTier = _.get(plan, `selectFeatures[${this.selectedFeatureIndex}]`);
            const selectedTierPrice = selectedTier
                ? selectedTier.isTrialFeature
                    ? _.get(selectedTier, `orjinalPrice`, 0)
                    : _.get(selectedTier, `price`, 0)
                : 0;
            price = selectedTier ? selectedTierPrice : price;
            let cloneDetails = _.cloneDeep(this.details || { agentCount: 0, whatsappChannel: 0 });
            if (clonePlan?.isTeamPlan) cloneDetails = {};
            let count = getProductQuantity(cloneDetails, clonePlan.type);
            if (!count) count = getProductQuantity({}, "default");
            const discount = calcDiscount(price, count, this.period);
            if (discount) price = discount;
            return price;
        },
        onSelectedFeature(val) {
            this.selectedFeatureIndex = val;
        },
        gotoContactSales() {
            window.open(this.plan.enterPriseUrl, "_blank");
        },
        gotoGroupPlan() {
            this.$router.push(`/billing/plans/${this.plan.type}-team`);
        },
        subscribePlan() {
            if (this.isBlockedByResourceLimit) {
                this.usageWarningVisible = true;
                return;
            }
            if (!this.isUpgrade) return;
            if (this.isActive && (this.wizard || this.currentPlansHash[this.plan._id]?.enabled)) {
                this.unsubscribe(this.plan);
            } else {
                this.subscribe(this.plan);
            }
        },
        subscribe(plan) {
            let payload = { planId: plan._id, period: this.period, isFreePlan: plan.isFreePlan };
            if (plan.type === "supsis") {
                payload.details = {
                    agentCount: plan.isFreePlan ? 1 : parseInt(this.details.agentCount),
                };
            } else if (plan.type === "whatsapp" || plan.type === "whatsapp-business") {
                payload.details = {
                    whatsappChannel: parseInt(this.details.whatsappChannel),
                };
            } else if (plan.type === "instagram") {
                payload.details = {
                    instagramChannel: parseInt(this.details.instagramChannel),
                };
            } else if (plan.type === "messenger") {
                payload.details = {
                    messengerChannel: parseInt(this.details.messengerChannel),
                };
            } else if (plan.type === "trendyol") {
                payload.details = {
                    trendyolChannel: parseInt(this.details.trendyolChannel),
                };
            } else if (plan.type === "meta-lead") {
                payload.details = {
                    metaLeadChannel: parseInt(this.details.metaLeadChannel),
                };
            } else if (plan.type === "telegram") {
                payload.details = {
                    telegramChannel: parseInt(this.details.telegramChannel),
                };
            } else if (plan.type === "linkedin") {
                payload.details = {
                    linkedinChannel: this.details.linkedinChannel,
                };
            } else if (plan.type === "automation") {
                payload.details = {
                    maxUsageCount: +this.plan.selectFeatures[this.selectedFeatureIndex]?.count || 0,
                };
            } else if (plan.type === "mail") {
                const selectedFeature = this.plan.selectFeatures[this.selectedFeatureIndex];
                const maxUsageCount = selectedFeature?.count || 0;
                const maxEmailSentContact = selectedFeature.details?.uniqEmailContactCount || 0;
                payload.details = {
                    maxUsageCount,
                    maxEmailSentContact,
                };
            } else if (plan.type === "web") {
                const selectedFeature = this.plan.selectFeatures[this.selectedFeatureIndex];
                const maxUsageCount = selectedFeature?.count || 0;
                payload.details = {
                    maxUsageCount,
                };
            } else if (plan.type === "webphone") {
                payload.details = {
                    channelCount: parseInt(this.details.channelCount),
                };
            }
            const price = this.getPrice(plan, payload?.details, this.period);
            const oldPrice = this.getDiscount(plan);
            this.$emit("subscribe", {
                ...payload,
                price: oldPrice,
                discount: Math.max(oldPrice - price, 0),
                couponCode: this.couponCode,
            });
        },
        unsubscribe(plan) {
            this.$emit("unsubscribe", {
                planId: plan._id,
                planType: plan.dynamicType || plan.type,
            });
        },
        editPlan() {
            this.$emit("edit-action", this.plan);
        },
        deletePlan() {
            this.$emit("delete-action", this.plan);
        },
        isHidden() {
            return this.visibilityDetails?.[this.plan._id];
        },
        visibilityChange() {
            this.$emit("visibilityChanged", this.plan, !this.isHidden());
        },
        getPlanFeatures() {
            const features = this.plan.features.reduce((acc, feature) => {
                const featureItems = Object.keys(feature).reduce((acc, item) => {
                    if (item !== "type") {
                        acc[item] = feature[item];
                    }
                    return acc;
                }, {});
                return { ...acc, ...featureItems };
            }, {});

            const orderedFeatures = Object.keys(features).reduce(
                (acc, feature) => {
                    acc[feature] = features[feature];
                    return acc;
                },
                {
                    webTrafficEnabled: null,
                    webTrafficAnalytics: null,
                    agentCount: null,
                    channelCount: null,
                    businessChannelCount: null,
                    instagramChannelCount: null,
                    messengerChannelCount: null,
                    trendyolChannelCount: null,
                    metaLeadChannelCount: null,
                    freeAgentCount: null,
                    voiceCalling: null,
                    videoCalling: null,
                    fileAndVoiceMessage: null,
                    detailedBroadcastReport: null,
                    chatCopilot: null,
                    liveChat: null,
                    liveScore: null,
                    multipleChatWindow: null,
                    shareScreen: null,
                    autoTranslation: null,
                    maxDiskSize: null,
                    freeAIToken: null,
                    chatbotEntegration: null,
                    maxTriggerCount: null,
                    chatArchiveTime: null,
                    maxSessionCount: null,
                    storyCountLimit: null,
                    integrationCount: null,
                    versionHistoryCount: null,
                    chatbotButtonActivity: null,
                    activeChatbotLimit: null,
                    allEntegrations: null,
                    teamChatEntegration: null,
                    whatsappEntegration: null,
                    whatsappChatbotEntegration: null,
                    whatsappVoiceCalling: null,
                    instagramEntegration: null,
                    messengerEntegration: null,
                    telegramEntegration: null,
                    smsEntegration: null,
                    webhookEntegration: null,
                    calenderEntegration: null,
                    automationEntegration: null,
                    mailEntegration: null,
                    taskManagementEntegration: null,
                    mailFreeCount: null,
                    mailCount: null,
                    readyContentsLimit: null,
                    readyContentsImage: null,
                    departmentLimit: null,
                    roleLimit: null,
                    formEntegration: null,
                    formLimit: null,
                    formSubmitLimit: null,
                    formViewLimit: null,
                    formFieldLimit: null,
                    customerTablesEntegration: null,
                    customerTableCount: null,
                    customerTableFieldCount: null,
                    customerTableRecordCount: null,
                    customerTableViewCount: null,
                    customerTablesAIFeature: null,
                    customerTablesLinkedFeature: null,
                    customerTableModuleFeature: null,
                    customerTableReportFeature: null,
                    taskWorkflowLimit: null,
                    taskColumnLimit: null,
                    taskRowLimit: null,
                    automationLimit: null,
                    automationTaskFreeLimit: null,
                    automationEventBase: null,
                    automationWebhookBase: null,
                    automationButtonBase: null,
                    automationTimerBase: null,
                    automationBlock: null,
                    report: null,
                    visitorTracking: null,
                    specialReport: null,
                    support: null,
                    softwareSupport: null,
                    dedicatedCustomerSupport: null,
                    supportWhatsappBusinessSetup: null,
                    supportFacebookSetup: null,
                    supportInstagramSetup: null,
                    supportChatbotSetup: null,
                    supportAISetup: null,
                    supportAutomationSetup: null,
                    supportSpecialPanelTraining: null,
                    supportConsultancy: null,
                    supportIntegrationSetup: null,
                    supportStrategyPlanning: null,
                    ticketReceivingLimit: null,
                    crmContactLimit: null,
                    chatAndNotification: null,
                    chatBalloonCustomization: null,
                    removeSupsisLogo: null,
                    databaseConnection: null,
                    singlePanelAccess: null,
                    ticketSystem: null,
                    calendarWebFeature: null,
                    accessAllAgent: null,
                    visitorStartChat: null,
                    dataProtect: null,
                    readyContents: null,
                    permissionControl: null,
                    autoRezervation: null,
                    flexibleBilling: null,
                    broadcastPerMessage: null,
                    bulkMessageSending: null,
                    messageUnregisteredNumbers: null,
                    personalizedBroadcastMessage: null,
                    controlWhatsapp: null,
                    notAdditionalPrice: null,
                    instagramManageInbox: null,
                    instagramCommentManagement: null,
                    instagramPrivateReplyMessage: null,
                    instagramMentionTicket: null,
                    instagramManageWithChatbot: null,
                    trendyolQuestionAnswer: null,
                    trendyolAutomaticAnswer: null,
                    trendyolCommentChatbotManage: null,
                    metaLeadAutomationManage: null,
                    metaLeadTicketManage: null,
                    mailPerCount: null,
                    mailPerPrice: null,
                    flexiblePricing: null,
                    unlimitedCBScenario: null,
                    freeFirstScenario: null,
                    rightToCancel: null,
                    unlimitedLearn: null,
                    allPlatformIntegration: null,
                    aiSupport: null,
                    creativeAI: null,
                    unlimitedSession: null,
                    assistantFromWebsite: null,
                    assistantFromDoc: null,
                    mobileApp: null,
                    whatsapp: null,
                    email: null,
                    web: null,
                    instagram: null,
                    messenger: null,
                    apiUsage: null,
                    excelDownload: null,
                    customerGroup: null,
                    // Voice Agent özellikleri
                    voiceAgentActiveCount: null,
                    voiceAgentBulkCallCount: null,
                    voiceAgentVoiceCloning: null,
                    voiceAgentConcurrentCallCount: null,
                    voiceAgentAppointmentTracking: null,
                    voiceAgentReadyVoiceOpenAI: null,
                    voiceAgentCommunityVoice: null,
                    voiceAgentWebAgent: null,
                    voiceAgentChatbotCall: null,
                    voiceAgentAutomationCall: null,
                    voiceAgentTableCall: null,
                    voiceAgentContactCall: null,
                    voiceAgentLlmSupport: null,
                    voiceAgentCustomLlmIntegration: null,
                    voiceAgentCustomDataTraining: null,
                    voiceAgentIvr: null,
                    voiceAgentCustomFunctions: null,
                    voiceAgentSentimentAnalysis: null,
                    voiceAgentAdvancedVoiceSettings: null,
                    voiceAgentSecretaryDetection: null,
                    voiceAgentCustomPrompts: null,
                    // Webphone özellikleri
                    webphoneChannelCount: null,
                    webphoneIntegratedWebPhone: null,
                    webphoneClickToCall: null,
                    webphoneCallHistory: null,
                    webphoneGeneralCallRecords: null,
                    webphoneUserExtensionMapping: null,
                    webphoneAutomations: null,
                    webphoneExtensionListing: null,
                    webphoneInternalCalling: null,
                    webphoneContactCallHistory: null,
                    webphoneCallProfileDisplay: null,
                    webphoneVoiceRecordingStorage: null,
                    webphoneCallRouting: null,
                    webphoneColdCallStart: null,
                    webphoneAutomationColdCall: null,
                    webphoneIvr: null,
                    webphoneVoiceAnnouncement: null,
                    webphoneSatisfactionSurveyCall: null,
                    webphoneCustomizableCloudPbx: null,
                    webphoneLiveQueueMonitoring: null,
                    webphoneAiVoiceAgentIntegration: null,
                    webphoneMultipleWebPhone: null,
                }
            );
            return Object.keys(orderedFeatures)
                .filter((feature) => {
                    return (
                        (typeof orderedFeatures[feature] === "number" ? orderedFeatures[feature] !== 0 : true) &&
                        orderedFeatures[feature]
                    );
                })
                .reduce((acc, feature) => {
                    acc[feature] = orderedFeatures[feature];
                    return acc;
                }, {});
        },
    },
    watch: {
        plans: {
            handler() {
                this.initData();
            },
            deep: true,
        },
        currentPlans: {
            handler() {
                this.initData();
                const currentPlan = this.currentPlansHash[this.plan._id];
                const selectedIndex = this.plan.selectFeatures?.findIndex(
                    (selectFeature) => +selectFeature.count === +currentPlan?.details?.maxUsageCount
                );
                if (selectedIndex >= 0) this.selectedFeatureIndex = selectedIndex;
            },
        },
        plan: {
            handler() {
                this.features = this.getPlanFeatures();
            },
            deep: true,
        },
    },
};
</script>
<style lang="scss" scoped>
.supsis-plan-card {
    border: 1px solid #e2e2e2;
    border-radius: 8px;
    position: relative;
    min-height: 400px;
    width: 275px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    align-self: flex-start;

    &.selected,
    &:hover {
        border-color: #222222;
    }

    &-head {
        width: 100%;
        padding: 15px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 15px;

        .plan-badge {
            background: var(--color-icon-primary-default);
            padding: 4px 20px;
            color: white;
            border-radius: 6px;
            text-wrap: nowrap;
        }

        &-title {
            width: 100%;
            min-height: 30px;
            display: flex;
            justify-content: space-between;
            align-items: center;

            .plan-title {
                font-size: 16px;
                font-weight: 500;
                color: #222222;
            }
        }

        &-pricing {
            display: flex;
            flex-direction: column;
            height: 70px;

            &-detail {
                font-size: 30px;
                font-weight: 500;
                color: #222222;

                .discount {
                    font-size: 14px;
                    color: var(--color-icon-primary-default);
                    text-decoration: line-through;
                    margin-left: 5px;
                }
            }

            &-period {
                font-size: 14px;
                color: #222222;
            }
        }

        &.popular {
            background-color: #fff4f2;
        }

        &-actions {
            display: flex;
            flex-direction: column;
            gap: 5px;

            .supsis-button {
                min-width: 100%;
            }
        }
    }

    &-body {
        padding: 10px;
        border-top: 1px solid #e2e2e2;
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 10px;

        &-content {
            font-size: 16px;
            font-weight: 500;
            color: #222222;
        }

        &-features {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }
    }

    &-footer {
        border-top: 1px solid #e2e2e2;
        width: 100%;
        padding: 10px;
        color: #8796af;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 4px;

        .material-icons {
            font-size: 20px;
        }
    }
}
</style>
